import React, { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Search, ArrowUpDown, User, Archive } from "lucide-react";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { StatusBadge, PriorityBadge, DeadlineBadge, getDeadlineUrgency } from "../shared/Badge";
import { UserAvatar } from "../shared/UserAvatar";
import { EmptyState } from "../shared/EmptyState";
import { PageLoader } from "../shared/LoadingSpinner";
import {
    formatRelativeTime,
    getTicketTypeConfig,
    TICKET_TYPES,
    STATUSES,
    PRIORITIES,
} from "../../lib/utils";

const SORT_OPTIONS = [
    { id: "created_at", label: "Date de création" },
    { id: "priority", label: "Priorité" },
    { id: "deadline", label: "Deadline" },
];

const PRIORITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };

export function AdminTicketQueue() {
    const { currentUser, getUserById, users, departments, hasPermission, wsClient } = useApp();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    // Filters from URL
    const statusFilter = searchParams.get("status") || "";
    const typeFilter = searchParams.get("type") || "";
    const priorityFilter = searchParams.get("priority") || "";
    const assigneeFilter = searchParams.get("assignee") || "";
    const departmentFilter = searchParams.get("department") || "";
    const archiveView = searchParams.get("archive") || "active"; // active | archived | all
    const mineOnly = searchParams.get("mine") === "true";
    const sortBy = searchParams.get("sort") || "created_at";

    useEffect(() => {
        loadTickets();
    }, [statusFilter, typeFilter, priorityFilter, assigneeFilter, departmentFilter, mineOnly, archiveView]);

    // 10B — WebSocket: auto-refresh queue on ticket events
    useEffect(() => {
        if (!wsClient) return;
        const refresh = () => loadTickets();
        const unsub1 = wsClient.on("ticket_created", refresh);
        const unsub2 = wsClient.on("ticket_updated", refresh);
        const unsub3 = wsClient.on("ticket_assigned", refresh);
        const unsub4 = wsClient.on("ticket_archived", refresh);
        const unsub5 = wsClient.on("ticket_deleted", refresh);
        return () => { unsub1(); unsub2(); unsub3(); unsub4(); unsub5(); };
    }, [wsClient]);

    const loadTickets = async () => {
        try {
            setLoading(true);
            const params = { limit: 200 };
            if (statusFilter) params.status = statusFilter;
            if (typeFilter) params.type = typeFilter;
            if (priorityFilter) params.priority = priorityFilter;
            if (mineOnly && currentUser?.id) {
                params.assigned_to_id = currentUser.id;
            } else if (assigneeFilter) {
                params.assigned_to_id = assigneeFilter;
            }
            if (departmentFilter) params.department_id = departmentFilter;
            // Archive filtering
            if (archiveView === "archived") {
                params.archived_only = true;
            } else if (archiveView === "all") {
                params.include_archived = true;
            }
            // default "active" → no archive params (backend excludes archived by default)

            const data = await api.getTickets(params);
            setTickets(data);
        } catch (error) {
            console.error("Failed to load tickets:", error);
        } finally {
            setLoading(false);
        }
    };

    const updateFilter = (key, value) => {
        const newParams = new URLSearchParams(searchParams);
        if (value && value !== "all") {
            newParams.set(key, value);
        } else {
            newParams.delete(key);
        }
        setSearchParams(newParams);
    };

    const toggleMine = () => {
        const newParams = new URLSearchParams(searchParams);
        if (mineOnly) {
            newParams.delete("mine");
        } else {
            newParams.set("mine", "true");
            newParams.delete("assignee"); // clear assignee filter
        }
        setSearchParams(newParams);
    };

    // Filter + sort
    const processedTickets = useMemo(() => {
        let result = tickets.filter((ticket) => {
            if (!searchQuery) return true;
            const query = searchQuery.toLowerCase();
            return (
                ticket.title.toLowerCase().includes(query) ||
                ticket.ticket_number.toLowerCase().includes(query)
            );
        });

        // Sort
        if (sortBy === "deadline") {
            result = [...result].sort((a, b) => {
                const urgA = getDeadlineUrgency(a.deadline_info);
                const urgB = getDeadlineUrgency(b.deadline_info);
                // Overdue first, then by remaining_seconds ascending (closest deadline first)
                if (urgA.state === "overdue" && urgB.state !== "overdue") return -1;
                if (urgB.state === "overdue" && urgA.state !== "overdue") return 1;
                const secA = a.deadline_info?.remaining_seconds ?? 999999999;
                const secB = b.deadline_info?.remaining_seconds ?? 999999999;
                return secA - secB;
            });
        } else if (sortBy === "priority") {
            result = [...result].sort((a, b) => {
                return (PRIORITY_ORDER[a.priority] ?? 3) - (PRIORITY_ORDER[b.priority] ?? 3);
            });
        }
        // default: created_at (already sorted from backend)

        return result;
    }, [tickets, searchQuery, sortBy]);

    const agents = users.filter((u) => u.role === "admin" || u.role === "agent");

    const myCount = tickets.filter((t) => t.assigned_to_id === currentUser?.id).length;

    return (
        <div className="page-container" data-testid="admin-ticket-queue">
            <div className="page-header">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="page-title">
                            {mineOnly ? "Mes tickets" : "File de tickets"}
                        </h1>
                        <p className="page-subtitle">
                            {processedTickets.length} ticket{processedTickets.length !== 1 ? "s" : ""}
                            {mineOnly && ` assigné${processedTickets.length !== 1 ? "s" : ""} à vous`}
                        </p>
                    </div>
                    <Button
                        variant={mineOnly ? "default" : "outline"}
                        size="sm"
                        onClick={toggleMine}
                        className="gap-2"
                        data-testid="toggle-mine"
                    >
                        <User className="w-4 h-4" />
                        Mes tickets ({myCount})
                    </Button>
                </div>
            </div>

            {/* Archive Filter Tabs */}
            <div className="flex items-center gap-1 mb-4" data-testid="archive-filter-tabs">
                {[
                    { key: "active", label: "Actifs" },
                    { key: "archived", label: "Archivés" },
                    { key: "all", label: "Tous" },
                ].map((tab) => (
                    <button
                        key={tab.key}
                        onClick={() => updateFilter("archive", tab.key === "active" ? "" : tab.key)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                            archiveView === tab.key
                                ? "bg-gray-900 text-white"
                                : "bg-white text-gray-600 hover:bg-gray-100 border"
                        }`}
                        data-testid={`archive-tab-${tab.key}`}
                    >
                        {tab.key === "archived" && <Archive className="w-3.5 h-3.5 inline mr-1.5" />}
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Filters */}
            <div className="card mb-6">
                <div className="card-body">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex-1 min-w-[200px] max-w-md">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <Input
                                    placeholder="Rechercher..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-9"
                                    data-testid="search-input"
                                />
                            </div>
                        </div>
                        <Select value={statusFilter || "all"} onValueChange={(v) => updateFilter("status", v)}>
                            <SelectTrigger className="w-[150px]" data-testid="filter-status">
                                <SelectValue placeholder="Statut" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous statuts</SelectItem>
                                {STATUSES.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={typeFilter || "all"} onValueChange={(v) => updateFilter("type", v)}>
                            <SelectTrigger className="w-[150px]" data-testid="filter-type">
                                <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous types</SelectItem>
                                {TICKET_TYPES.map((t) => (
                                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={priorityFilter || "all"} onValueChange={(v) => updateFilter("priority", v)}>
                            <SelectTrigger className="w-[130px]" data-testid="filter-priority">
                                <SelectValue placeholder="Priorité" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Toutes</SelectItem>
                                {PRIORITIES.map((p) => (
                                    <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {!mineOnly && (
                            <Select value={assigneeFilter || "all"} onValueChange={(v) => updateFilter("assignee", v)}>
                                <SelectTrigger className="w-[150px]" data-testid="filter-assignee">
                                    <SelectValue placeholder="Assigné" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous agents</SelectItem>
                                    <SelectItem value="unassigned">Non assigné</SelectItem>
                                    {agents.map((a) => (
                                        <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        {/* Sort */}
                        <Select value={sortBy} onValueChange={(v) => updateFilter("sort", v)}>
                            <SelectTrigger className="w-[160px]" data-testid="sort-by">
                                <SelectValue>
                                    <span className="flex items-center gap-1.5">
                                        <ArrowUpDown className="w-3.5 h-3.5" />
                                        {SORT_OPTIONS.find((s) => s.id === sortBy)?.label}
                                    </span>
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                {SORT_OPTIONS.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Ticket List */}
            {loading ? (
                <PageLoader />
            ) : processedTickets.length === 0 ? (
                <div className="card">
                    <EmptyState
                        title="Aucun ticket"
                        description={mineOnly ? "Aucun ticket ne vous est assigné" : "Aucun ticket ne correspond à vos critères"}
                    />
                </div>
            ) : (
                <div className="card">
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Ticket</th>
                                    <th>Type</th>
                                    <th>Demandeur</th>
                                    <th>Statut</th>
                                    <th>Deadline</th>
                                    <th>Priorité</th>
                                    <th>Assigné</th>
                                    <th>Créé</th>
                                </tr>
                            </thead>
                            <tbody>
                                {processedTickets.map((ticket) => {
                                    const typeConfig = getTicketTypeConfig(ticket.type);
                                    const requester = getUserById(ticket.requester_id);
                                    const assignee = getUserById(ticket.assigned_to_id);
                                    const ticketIsArchived = !!ticket.archived_at;
                                    return (
                                        <tr
                                            key={ticket.id}
                                            className={`cursor-pointer hover:bg-gray-50 ${ticketIsArchived ? "opacity-60 bg-gray-50" : ""}`}
                                            onClick={() => navigate(`/tickets/${ticket.id}`)}
                                            data-testid={`ticket-row-${ticket.id}`}
                                        >
                                            <td>
                                                <div className="flex flex-col">
                                                    <span className="text-xs font-mono text-gray-400">{ticket.ticket_number}</span>
                                                    <span className="font-medium text-gray-900 truncate max-w-[300px]">{ticket.title}</span>
                                                </div>
                                            </td>
                                            <td><span className="text-sm text-gray-600">{typeConfig.label}</span></td>
                                            <td>
                                                {requester && (
                                                    <div className="flex items-center gap-2">
                                                        <UserAvatar user={requester} size="sm" />
                                                        <span className="text-sm">{requester.first_name}</span>
                                                        {requester.user_type === "external" && <span className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-semibold bg-amber-100 text-amber-700">EXT</span>}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div className="flex items-center gap-1.5">
                                                    <StatusBadge status={ticket.status} />
                                                    {ticketIsArchived && (
                                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-200 text-gray-600" data-testid={`archived-badge-${ticket.id}`}>
                                                            <Archive className="w-2.5 h-2.5" />Archivé
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td><DeadlineBadge info={ticket.deadline_info} /></td>
                                            <td><PriorityBadge priority={ticket.priority} /></td>
                                            <td>
                                                {assignee ? (
                                                    <div className="flex items-center gap-2">
                                                        <UserAvatar user={assignee} size="sm" />
                                                        <span className="text-sm">{assignee.first_name}</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400 text-sm">—</span>
                                                )}
                                            </td>
                                            <td><span className="text-sm text-gray-500">{formatRelativeTime(ticket.created_at)}</span></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
